import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';

const beforeImages = [
  {
    src: 'https://customer-assets.emergentagent.com/job_renovations-pro-1/artifacts/7e6ahd9b_1.jpg',
    alt: 'Marco de puerta dañado en baño',
    caption: 'Marcos y terminaciones deterioradas',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_renovations-pro-1/artifacts/r4lnw81g_2.jpg',
    alt: 'Pared con manchas y calefactor suelto',
    caption: 'Muros manchados y equipamiento dañado',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_renovations-pro-1/artifacts/crqsap4i_3.jpg',
    alt: 'Cocina con gabinete faltante y suciedad',
    caption: 'Cocinas incompletas y sucias',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_renovations-pro-1/artifacts/sgv8grv9_4.jpg',
    alt: 'Balcón con muros sucios y enchufes dañados',
    caption: 'Espacios con desgaste general',
  },
];

const afterImages = [
  {
    src: 'https://customer-assets.emergentagent.com/job_renovations-pro-1/artifacts/8r9iub17_1.jpg',
    alt: 'Muro pintado impecable',
    caption: 'Muros impecables y renovados',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_renovations-pro-1/artifacts/069v3boc_2.jpg',
    alt: 'Puerta y marco renovados',
    caption: 'Puertas y marcos como nuevos',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_renovations-pro-1/artifacts/1durznw1_3.jpg',
    alt: 'Baño renovado con cerámicas limpias',
    caption: 'Baños limpios y renovados',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_renovations-pro-1/artifacts/wrj88pfc_4.jpg',
    alt: 'Terminaciones y molduras renovadas',
    caption: 'Terminaciones de primer nivel',
  },
];

function ImageGrid({ images, isBefore, isInView }) {
  const [current, setCurrent] = useState(0);
  const next = () => setCurrent((p) => (p + 1) % images.length);
  const prev = () => setCurrent((p) => (p - 1 + images.length) % images.length);

  return (
    <div className="border border-r2r-border">
      {/* Desktop grid */}
      <div className="hidden md:grid grid-cols-2 gap-3 p-3">
        {images.map((img, i) => (
          <div
            key={i}
            data-testid={`${isBefore ? 'before' : 'after'}-image-${i}`}
            className={`relative overflow-hidden group rounded-sm`}
          >
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src={img.src}
                alt={img.alt}
                className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${
                  isBefore ? 'grayscale-[30%] group-hover:grayscale-0' : ''
                }`}
              />
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent">
              <p className="font-manrope text-xs text-white/90 font-medium">{img.caption}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Mobile carousel */}
      <div className="md:hidden relative">
        <div className="aspect-[4/3] overflow-hidden">
          <img
            src={images[current].src}
            alt={images[current].alt}
            data-testid={`${isBefore ? 'before' : 'after'}-mobile-image`}
            className={`w-full h-full object-cover ${isBefore ? 'grayscale-[30%]' : ''}`}
          />
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent">
            <p className="font-manrope text-xs text-white/90 font-medium">
              {images[current].caption}
            </p>
          </div>
        </div>
        <div className="absolute top-1/2 -translate-y-1/2 left-2 right-2 flex justify-between pointer-events-none">
          <button
            onClick={prev}
            data-testid={`${isBefore ? 'before' : 'after'}-prev-btn`}
            className="pointer-events-auto w-8 h-8 flex items-center justify-center bg-white/80 text-r2r-text hover:bg-white transition-colors"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={next}
            data-testid={`${isBefore ? 'before' : 'after'}-next-btn`}
            className="pointer-events-auto w-8 h-8 flex items-center justify-center bg-white/80 text-r2r-text hover:bg-white transition-colors"
          >
            <ChevronRight size={16} />
          </button>
        </div>
        <div className="flex justify-center gap-2 py-3">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              data-testid={`${isBefore ? 'before' : 'after'}-dot-${i}`}
              className={`w-2 h-2 rounded-full transition-colors ${
                i === current ? 'bg-r2r-primary' : 'bg-r2r-border'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function BeforeAfterSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section
      id="resultados"
      data-testid="before-after-section"
      ref={ref}
      className="py-12 lg:py-16 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <span className="font-manrope text-xs font-bold uppercase tracking-[0.2em] text-r2r-sage mb-4 block">
            Resultados reales
          </span>
          <h2
            data-testid="before-after-heading"
            className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-r2r-text"
          >
            Antes y Después
          </h2>
          <p className="font-manrope text-base md:text-lg text-r2r-text-secondary mt-4 max-w-2xl leading-relaxed">
            Cada departamento pasa por una transformación integral. Así es como los recibimos y cómo los entregamos.
          </p>
        </motion.div>

        {/* Before Section */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="border border-r2r-border border-b-0 p-4 bg-r2r-bg">
            <span className="font-manrope text-xs font-bold uppercase tracking-[0.2em] text-r2r-sage">
              Lo que recibimos
            </span>
          </div>
          <ImageGrid images={beforeImages} isBefore={true} isInView={isInView} />
          <div className="border border-t-0 border-r2r-border p-6 lg:p-8">
            <p className="font-manrope text-sm text-r2r-text-secondary leading-relaxed">
              Departamentos con desgaste por uso, marcos dañados, muros manchados, cocinas incompletas y espacios que necesitan una renovación integral.
            </p>
          </div>
        </motion.div>

        {/* Arrow Transition */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="flex items-center justify-center py-8"
        >
          <div className="flex items-center gap-3 text-r2r-sage">
            <div className="h-[1px] w-12 bg-r2r-border" />
            <ArrowRight size={18} />
            <span className="font-manrope text-xs font-bold uppercase tracking-[0.2em]">
              Transformación R2R
            </span>
            <ArrowRight size={18} />
            <div className="h-[1px] w-12 bg-r2r-border" />
          </div>
        </motion.div>

        {/* After Section */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <div className="border border-r2r-border border-b-0 p-4 bg-r2r-primary">
            <span className="font-manrope text-xs font-bold uppercase tracking-[0.2em] text-white/70">
              Lo que entregamos
            </span>
          </div>
          <ImageGrid images={afterImages} isBefore={false} isInView={isInView} />
          <div className="border border-t-0 border-r2r-border p-6 lg:p-8">
            <p className="font-manrope text-sm text-r2r-text-secondary leading-relaxed">
              Espacios completamente renovados, con terminaciones de primer nivel, listos para la entrada inmediata del nuevo arrendatario.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
